import gql from 'graphql-tag';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {
  ComplexPolymerInterfaceQuery,
  ComplexPolymerInterfaceQueryVariables,
} from './__generated/ComplexPolymerInterfaceQuery';
import {isComplexPolymerInterfaceId} from './ComplexPolymerSchemaInterfaceConstants';
import {
  isValidEntityTypeForComplexPolymerFeatures,
  isValidSchemaTypeForComplexPolymerFeatures,
} from './ComplexPolymerUtil';

export const COMPLEX_POLYMER_INTERFACE_QUERY = gql`
  query ComplexPolymerInterfaceQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymerSchemaConfiguration(schemaId: $schemaId) {
      ... on ComplexPolymerConfigInterface {
        id
        schemaInterface {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useComplexPolymerInterfaceQuery(
  entityId: string,
  schemaId: string | null
): SkippableBenchlingQueryResult<ComplexPolymerInterfaceQuery> {
  const result = useSkippableBenchlingQuery<
    ComplexPolymerInterfaceQuery,
    ComplexPolymerInterfaceQueryVariables
  >(COMPLEX_POLYMER_INTERFACE_QUERY, {
    variables: {schemaId: schemaId!},
    skip:
      schemaId == null ||
      !isValidSchemaTypeForComplexPolymerFeatures(schemaId) ||
      !isValidEntityTypeForComplexPolymerFeatures(entityId),
  });
  return result;
}

export default function useIsComplexPolymerQuery(entityId: string, schemaId: string | null): boolean {
  /**
   * Queries the backend to determine if an entity is a complex polymer or not.
   *
   * The entityId and schemaId are used to skip the query if the entity can't possibly be a complex polymer.
   * - If the schemaId is null or not of an entity schema, query is skipped.
   * - If the entityId is of an entity that doesn't support complex polymer features, query is skipped.
   */
  const result = useComplexPolymerInterfaceQuery(entityId, schemaId);
  return (
    result.type === QueryResultType.SUCCESS &&
    result.data.complexPolymerSchemaConfiguration != null &&
    !isInaccessibleInfo(result.data.complexPolymerSchemaConfiguration) &&
    result.data.complexPolymerSchemaConfiguration.schemaInterface != null &&
    isComplexPolymerInterfaceId(result.data.complexPolymerSchemaConfiguration.schemaInterface.id)
  );
}
